import Vue from "vue";
import vSelect from 'vue-select';
import { ValidationMessage, ValidatedInput, FormLabel } from "@/app/shared/components";
import TaskPopup from "./taskPopup.vue";
import EmployeePopup from "./employeePopup.vue";
import EmployeeSideView from "../../admin/employees/components/employeeSideView.vue";
import i18n from '@/i18n/i18n'
import { ManipulateElement } from "@/app/shared/ManipulateElement.js";

export default {
  props: {
    entries: {
      type: Array,
      default: () => {}
    },
    readOnly : {
      type: Boolean,
      default: false
    },
    companyId: {
      type: Number,
      required: true
    },
    listEmployees: {
      type: Array,
      required: true
    },
    comments: {
      type: String,
      required: false,
      default: ""
    }
  },
  components: {
    vSelect,
    ValidationMessage,
    ValidatedInput,
    FormLabel,
    TaskPopup,
    EmployeePopup,
    EmployeeSideView
  },
  data () {
    return {
      activeEmployeePrompt:false,
      activeTaskPrompt:false,
      tasks: [],
      isNewEmployeePrompt : false,
      isNewTaskPrompt : false,
      innerEntries : [],
      employees : [],
      selectedJobEntry: null,
      selectedRow: null,
      createEmployeeSidebarActive: false,
      newlyCreatedEmployee: null,
      hours: [],
      createdEmployeeSidebar: {},
      dailyReportComments: '',
      displayCommentsPopup: false
    }
  },
  mounted()
  {
    this.innerEntries = this.entries;
    this.fillTasks();
    this.fillEmployees();
    this.fillHours();
    this.dailyReportComments = this.comments;
  },
  watch: {
    entries () {
      this.innerEntries = this.entries;
      this.fillTasks();
      this.fillEmployees();
      this.fillHours();
      this.dailyReportComments = this.comments;
    }
  },
  computed: {
    employeePromptTitle() {
      return this.isNewEmployeePrompt ? this.$t('Drilling.JobEntries.AddTimeEntry') : this.$t('Drilling.JobEntries.EditTimeEntry');
    },
    listTitles() {
      return this.$store.state.drilling.employeeTitles;
    },
    companies() {
      return this.$store.state.drilling.companies;
    },
    categories() {
      return this.$store.state.drilling.taskCategories;
    },
    rows() {
      const rows = [];
      this.employees.forEach(employee => {
        const hours = [];
        this.tasks.forEach(task => {
          hours.push(this.getHours(employee.employeeId, employee.employeeName, employee.employeeTitleId, employee.title, employee.shift, task.taskId, task.otherTaskName, task.isBillable))
        })
        rows.push({
          ...employee,
          rowId: this.generateRowId(employee),
          hours
        });
      });
      // shift can be null on imported entries; concatenating it would build a "null..." key
      return rows.sort((a, b) => ((a.shift ?? '') + this.getEmployeeDescription(a)).localeCompare((b.shift ?? '') + this.getEmployeeDescription(b)));
    },
    columns() {
      return this.tasks.sort((a, b) => (this.getJobEntryTaskName(a) + "\u0000" + a.isBillable).localeCompare((this.getJobEntryTaskName(b) + "\u0000" + b.isBillable)));
    }
  },
  methods: {
    getJobEntryTaskName(entry) {
      if (entry.task) return entry.task[this.localizedName()];
      if (entry.taskId) {
        return this.$store.state.drilling.taskCategories.reduce((acc, category) => {
          return acc.concat(category.tasks);
        }, []).find(t => t.id === entry.taskId)[this.localizedName()];
      }
      return entry.otherTaskName;
    },
    localizedName() {
      return this.$i18n.locale.startsWith('fr') ? "nameFr" : "nameEn";
    },
    addTask() {
      this.isNewTaskPrompt = true;
      this.activeTaskPrompt = true;
      this.selectedJobEntry = null;
    },
    editTask(jobEntry) {
      this.selectedJobEntry = structuredClone(jobEntry);
      this.isNewTaskPrompt = false;
      this.activeTaskPrompt = true;
    },
    taskColumnHeader1(col) {
      if (col.otherTaskName && col.otherTaskName !== '') {
        return col.otherTaskName  + (col.isBillable ? "" : " " + i18n.t('Drilling.JobEntries.IsNonBillableShort'))
      }
      else if (col.taskId) {
        const task = this.findTask(col.taskId);
        return task[this.localizedName()] + ' (' + this.categories.find(_ => _.id === task.categoryId)[this.localizedName()] + ')' + (col.isBillable ? "" : " " + i18n.t('Drilling.JobEntries.IsNonBillableShort'))
      }
      else {
        return 'N/A' +  (col.isBillable ? "" : " " + i18n.t('Drilling.JobEntries.IsNonBillableShort'))
      }
    },
    taskColumnHeader2(col) {
      const task = this.findTask(col.taskId);
      return task && (col.otherTaskName && col.otherTaskName !== '') ? task[this.localizedName()] + ' (' + this.categories.find(_ => _.id === task.categoryId)[this.localizedName()] + ')' : '';
    },
    closeTaskPrompt() {
      this.activeTaskPrompt = false;
    },
    closeEmployeePrompt() {
      this.activeEmployeePrompt = false;
    },
    submitTask(newJobEntry) {
      if (this.isNewTaskPrompt) {
        this.tasks.push(newJobEntry);
      }
      else {
        const index = this.tasks.findIndex((e) => this.getJobEntryTaskName(e) === this.getJobEntryTaskName(this.selectedJobEntry) && e.isBillable === this.selectedJobEntry.isBillable)
        if (index !== undefined) {
          // change all hours associated with the previous task to the new one
          this.hours.forEach(h => {
            if (this.matchHourTask(h, this.tasks[index].taskId, this.tasks[index].otherTaskName, this.tasks[index].isBillable)) {
              h.taskId = newJobEntry.taskId;
              h.otherTaskName = newJobEntry.otherTaskName;
              h.isBillable = newJobEntry.isBillable;
            }
          });
          Vue.set(this.tasks, index, {...this.selectedJobEntry, ...newJobEntry})
        }
      }
    },
    removeTask(task) {
      const index = this.tasks.findIndex((t) => this.matchHourTask(t, task.taskId, task.otherTaskName, task.isBillable));
      this.tasks.splice(index, 1);
    },
    findTask(taskId) {
      return this.categories.map(_ => _.tasks).flat().find(_ => _.id === taskId);
    },
    addRow()
    {
      this.isNewEmployeePrompt = true;
      this.selectedRow = {
        hours:[],
        shift: 0,
        title: '',
        employeeTitleId: null,
        note: null,
        employeeName: null
      };
      this.activeEmployeePrompt = true;
      setTimeout(() => {
        this.elmnt = new ManipulateElement(".vs-dialog", document.getElementsByClassName("vs-dialog-header")[0], document.getElementsByClassName("vs-dialog-cancel")[0]);
      }, 500);
    },
    editRow(row) {
      this.isNewEmployeePrompt = false;
      this.selectedRow = row;
      this.activeEmployeePrompt = true;
      setTimeout(() => {
        this.elmnt = new ManipulateElement(".vs-dialog", document.getElementsByClassName("vs-dialog-header")[0], document.getElementsByClassName("vs-dialog-cancel")[0]);
      }, 500);
    },
    duplicateRow(row) {
      this.isNewEmployeePrompt = true;
      this.selectedRow = structuredClone(row);
      this.activeEmployeePrompt = true;
    },
    submitEmployee(row) {
      if (this.isNewEmployeePrompt) {
        row.rowId = this.generateRowId(row);
        this.employees.push(row);
        row.hours.forEach((hour, index) => {
          this.setHours(row.employeeId, row.employeeName, row.employeeTitleId, row.title, row.shift, this.columns[index].taskId, this.columns[index].otherTaskName, this.columns[index].isBillable, hour);
        });
      }
      else {
        const index = this.employees.findIndex((_) => this.matchEmployee(_, this.selectedRow))
        this.hours.forEach(h => {
          if (this.matchEmployee(h, this.employees[index])) {
            h.employeeId = row.employeeId;
            h.employeeName = row.employeeName;
            h.employeeTitleId = row.employeeTitleId;
            h.title = row.title;
            h.shift = row.shift;
          }
        });
        this.employees[index].employeeId = row.employeeId;
        this.employees[index].employeeTitleId = row.employeeTitleId;
        this.employees[index].shift = row.shift;
        this.employees[index].note = row.note;
      }
    },
    getEmployeeDescription(row) {
      const title = this.getTitle(row);
      if (this.listEmployees === null || this.listEmployees === undefined)
        return row.employeeName + " (" + title + ")";
      const employee = this.listEmployees.find(e => e.id === row.employeeId);
      let employeeName = row.employeeName || '';
      if (employee && employee.id !== null) {
        employeeName = employee.firstName + ' ' + employee.lastName + " (" + title + ")";
      }
      return employeeName;
    },
    removeRow(row) {
      const index = this.employees.findIndex((e) => this.matchEmployee(e, row));
      this.employees.splice(index, 1);
    },
    matchEmployee(employee1, employee2) {
      return this.matchHourEmployee(employee1, employee2.employeeId, employee2.employeeName, employee2.employeeTitleId, employee2.title, employee2.shift);
    },
    matchHourEmployee(h, employeeId, employeeName, employeeTitleId, title, shift) {
      return ((h.employeeId !== null && h.employeeId === employeeId) || (h.employeeId === null && this.compareStringCaseInsensitive(h.employeeName, employeeName))) &&
        ((h.employeeTitleId !== null && h.employeeTitleId === employeeTitleId) || (h.employeeTitleId === null && this.compareStringCaseInsensitive(h.title, title))) &&
        h.shift === shift
    },
    matchHourTask(h, taskId, otherTaskName, isBillable) {
      return ((h.taskId !== null && h.taskId === taskId) || (h.taskId === null && h.otherTaskName === otherTaskName)) &&
        h.isBillable === isBillable;
    },
    matchHour(h, employeeId, employeeName, employeeTitleId, title, shift, taskId, otherTaskName, isBillable) {
      return this.matchHourEmployee(h, employeeId, employeeName, employeeTitleId, title, shift) &&
      this.matchHourTask(h, taskId, otherTaskName, isBillable);
    },
    getHours(employeeId, employeeName, employeeTitleId, title, shift, taskId, otherTaskName, isBillable) {
      const index = this.hours.findIndex(h => this.matchHour(h, employeeId, employeeName, employeeTitleId, title, shift, taskId, otherTaskName, isBillable));
      if (index === -1)
        return 0;
      return this.hours[index].hours ?? 0;
    },
    setHours(employeeId, employeeName, employeeTitleId, title, shift, taskId, otherTaskName, isBillable, hours) {
      const index = this.hours.findIndex(h => this.matchHour(h, employeeId, employeeName, employeeTitleId, title, shift, taskId, otherTaskName, isBillable));
      if (index === -1)
        this.hours.push({employeeId, employeeName, employeeTitleId, title, shift, taskId, otherTaskName, isBillable, hours});
      else
        this.hours[index].hours = hours;
    },
    editHours(row, task, taskIndex) {
      let hour = row.hours[taskIndex];
      if (!isNaN(parseFloat(hour)))
        hour = parseFloat(hour);
      else
        hour = 0;
      this.setHours(row.employeeId, row.employeeName, row.employeeTitleId, row.title, row.shift, task.taskId, task.otherTaskName, task.isBillable, hour);
    },
    isShiftMissing(row) {
      // shift 0 (Day) is falsy, so a truthiness test would report it as missing
      return row.shift === null || row.shift === undefined;
    },
    getShift(row) {
      return this.isShiftMissing(row) ? "" : this.$t("Shift." + row.shift);
    },
    getTitle(row) {
      let title = row.title;
      if (row.employeeTitleId !== null) {
        const selectedTitle = this.listTitles.find(x => x.id === row.employeeTitleId);
        if (selectedTitle)
          title = selectedTitle[this.localizedName()];
      }
      return title;
    },
    fillTasks() {
      const tasks = this.innerEntries.map(({task, taskId, isBillable, otherTaskName, isOtherTask}) => ({ task, taskId, isBillable, otherTaskName, isOtherTask }));
      const seen = new Set();
      this.tasks = tasks.filter(entry => {
        const key = this.getJobEntryTaskName(entry) + "\u0000" + entry.isBillable;
        //                                ^---- a string you know won't be in either name or surname
        const keep = !seen.has(key);
        if (keep) {
          seen.add(key);
        }
        return keep;
      });
    },
    fillEmployees() {
      const employees = this.innerEntries.map(entry => ({
        employeeId: entry.employeeId,
        employeeTitleId: entry.employeeTitleId,
        shift: entry.shift,
        title: entry.title,
        employeeName: entry.employeeName,
        note: entry.note
      }));
      this.employees = employees.filter((value, index) => {
        return index === employees.findIndex(obj => {
          return value.employeeId === obj.employeeId &&
            value.employeeTitleId === obj.employeeTitleId &&
            value.shift === obj.shift &&
            this.compareStringCaseInsensitive(value.title, obj.title) &&
            this.compareStringCaseInsensitive(value.employeeName, obj.employeeName);
        });
      });
    },
    compareStringCaseInsensitive(a, b) {
      if (a === null && b !== null) return -1;
      if (b === null && a !== null) return -1;
      return (a === null && b === null) || a.toLowerCase() === b.toLowerCase();
    },
    fillHours() {
      this.innerEntries.forEach(entry => {
        this.setHours(entry.employeeId, entry.employeeName, entry.employeeTitleId, entry.title, entry.shift, entry.taskId, entry.otherTaskName, entry.isBillable, entry.hours);
      })
    },
    showCreateEmployeeSidebar() {
      this.createdEmployeeSidebar.companyId = this.companyId;
      this.createdEmployeeSidebar.isActive = true;

      this.createEmployeeSidebarActive = true;
      this.activeEmployeePrompt = false
    },
    hideCreateEmployeeSidebar() {
      this.createEmployeeSidebarActive = false
      this.activeEmployeePrompt = true
    },
    onEmployeeCreated(employee) {
      this.createEmployeeSidebarActive = false
      this.activeEmployeePrompt = true
      if (employee.isActive && employee.companyId === this.companyId)
        this.listEmployees.push(employee);
      this.newlyCreatedEmployee = employee;

      this.createdEmployeeSidebar = {}; // reset sidebar value
    },
    generateRowId(employee) {
      return employee.employeeId + '-' + employee.employeeTitleId + '-' + employee.shift;
    },
    openCommentsPopup() {
      this.displayCommentsPopup = true;
      this.elmnt = new ManipulateElement(".vs-popup", document.getElementsByClassName("vs-popup--header")[0], document.getElementsByClassName("vs-popup--close")[0]);
    },
    closeCommentsPopup() {
      this.displayCommentsPopup = false;
    }
  }
}
